import type { ActionsOf } from 'store/types';

import { notify } from 'utils/reduxUtils';

import * as types from './types';
import type {
  CheckEmailVerificationSuccessPayload,
  CreateTeamMemberRequestPayload,
  CreateTeamMemberSuccessPayload,
  ErrorPayload,
  ResendInviteRequestPayload,
  ResendInviteSuccessPayload,
  TeamMemberIdPayload,
  TeamMemberPayload,
  TeamMembersPayload,
  UpdateTeamMemberRequestPayload,
} from './types';

export const getTeamMembers = {
  request: () => ({
    type: types.GET_TEAM_MEMBERS_REQUEST,
  }),
  success: ({ teamMembers }: TeamMembersPayload) => ({
    type: types.GET_TEAM_MEMBERS_SUCCESS,
    payload: { teamMembers },
  }),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.GET_TEAM_MEMBERS_ERROR,
      payload: error,
    }),
    "Can't get team members",
  ),
};

export const getTeamMember = {
  success: ({ teamMember }: TeamMemberPayload) => ({
    type: types.GET_TEAM_MEMBER_SUCCESS,
    payload: { teamMember },
  }),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.GET_TEAM_MEMBER_ERROR,
      payload: error,
    }),
    "Can't get team member",
  ),
};

export const createTeamMember = {
  request: ({ teamMember }: CreateTeamMemberRequestPayload) => ({
    type: types.CREATE_TEAM_MEMBER_REQUEST,
    payload: { teamMember },
  }),
  success: notify(
    ({ response }: CreateTeamMemberSuccessPayload) => ({
      type: types.CREATE_TEAM_MEMBER_SUCCESS,
      payload: response,
    }),
    ({ payload }) => {
      if (payload.email) {
        if (payload.isNewUser) {
          return `Invitation sent to ${payload.email}`;
        } else {
          return `Added ${payload.email} to organization`;
        }
      } else {
        return '';
      }
    },
  ),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.CREATE_TEAM_MEMBER_ERROR,
      payload: error,
    }),
    "Can't add team member",
  ),
};

export const updateTeamMember = {
  request: ({ teamMember, cb }: UpdateTeamMemberRequestPayload) => ({
    type: types.UPDATE_TEAM_MEMBER_REQUEST,
    payload: { teamMember, cb },
  }),
  success: notify(
    ({ teamMember }: TeamMemberPayload) => ({
      type: types.UPDATE_TEAM_MEMBER_SUCCESS,
      payload: { teamMember },
    }),
    'Team member successfully updated',
  ),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.UPDATE_TEAM_MEMBER_ERROR,
      payload: error,
    }),
    "Can't update team member",
  ),
};

export const deleteTeamMember = {
  request: ({ teamMemberId }: TeamMemberIdPayload) => ({
    type: types.DELETE_TEAM_MEMBER_REQUEST,
    payload: { teamMemberId },
  }),
  success: notify(
    ({ teamMemberId }: TeamMemberIdPayload) => ({
      type: types.DELETE_TEAM_MEMBER_SUCCESS,
      payload: { teamMemberId },
    }),
    'Team member successfully deleted',
  ),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.DELETE_TEAM_MEMBER_ERROR,
      payload: error,
    }),
    "Can't delete team member",
  ),
};

export const resendInvite = {
  request: ({ emailAddress }: ResendInviteRequestPayload) => ({
    type: types.TEAM_MEMBER_INVITE_RESEND_REQUEST,
    payload: { emailAddress },
  }),
  success: notify(
    ({ response }: ResendInviteSuccessPayload) => ({
      type: types.TEAM_MEMBER_INVITE_RESEND_SUCCESS,
      payload: response,
    }),
    ({ payload }) =>
      payload.email ? `Invitation sent to ${payload.email}` : '',
  ),
  error: notify(
    (error: unknown) => ({
      type: types.TEAM_MEMBER_INVITE_RESEND_ERROR,
      payload: error,
    }),
    'Failed to resend invite.',
  ),
};

export const initCreateTeamMember = (payload?: unknown) => ({
  type: types.CREATE_TEAM_MEMBER,
  payload: payload,
});

export const sendVerificationEmail = {
  request: ({ teamMemberId }: TeamMemberIdPayload) => ({
    type: types.SEND_VERIFICATION_EMAIL_REQUEST,
    payload: { teamMemberId },
  }),
  success: notify(
    () => ({
      type: types.SEND_VERIFICATION_EMAIL_SUCCESS,
    }),
    'Verification email sent.',
  ),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.SEND_VERIFICATION_EMAIL_ERROR,
      payload: error,
    }),
    'Something went wrong when sending the verification email.',
  ),
};

export const checkEmailVerification = {
  request: ({ teamMemberId }: TeamMemberIdPayload) => ({
    type: types.CHECK_EMAIL_VERIFICATION_REQUEST,
    payload: { teamMemberId },
  }),
  success: ({ isVerified }: CheckEmailVerificationSuccessPayload) => ({
    type: types.CHECK_EMAIL_VERIFICATION_SUCCESS,
    payload: { isVerified },
  }),
  error: notify(
    ({ error }: ErrorPayload) => ({
      type: types.CHECK_EMAIL_VERIFICATION_ERROR,
      payload: error,
    }),
    'Something went wrong when checking email verification.',
  ),
};

export type SupplierAction = ActionsOf<{
  checkEmailVerification: typeof checkEmailVerification;
  createTeamMember: typeof createTeamMember;
  deleteTeamMember: typeof deleteTeamMember;
  getTeamMember: typeof getTeamMember;
  getTeamMembers: typeof getTeamMembers;
  initCreateTeamMember: typeof initCreateTeamMember;
  resendInvite: typeof resendInvite;
  sendVerificationEmail: typeof sendVerificationEmail;
  updateTeamMember: typeof updateTeamMember;
}>;
